/**
 * Icon-to-Asset-Class Mapping Utility
 *
 * Maps icon identifiers to asset classes for filtering/sorting.
 * Uses Approach D: Auto-map + Manual Overrides
 *
 * @example
 * getAssetClassesForIcon('ad:User') // ['user']
 * getAssetClassesForIcon('aws:Lambda') // ['aws']
 */

/**
 * Remove only the category scope prefix from an icon ID.
 * Preserves the source:name format needed for class mapping.
 *
 * @param iconId - Potentially scoped ID (e.g., 'internet::ad:User')
 * @returns ID without category scope (e.g., 'ad:User')
 *
 * @example
 * removeCategoryScope('internet::ad:User') // 'ad:User'
 * removeCategoryScope('ad:User') // 'ad:User' (unchanged)
 */
function removeCategoryScope(iconId: string): string {
  const scopeIndex = iconId.indexOf('::');
  return scopeIndex === -1 ? iconId : iconId.slice(scopeIndex + 2);
}

/**
 * Manual overrides for icons with non-standard class mappings.
 * Auto-mapping handles the common case (source + lowercase name).
 */
const ICON_CLASS_OVERRIDES = {
  // AD icons with non-obvious mappings
  'ad:DomainController': ['domain'],
  'ad:CertificateAuthority': ['enterpriseca', 'rootca', 'certtemplate'],
  'ad:GroupPolicy': ['gpo'],
  'ad:OrganizationalUnit': ['ou'],

  // Infrastructure with multiple class mappings
  'infrastructure:Server': ['ipv4', 'ipv6'],
  'infrastructure:WebApp': ['webapplication', 'domain'],
} as const satisfies Record<string, readonly string[]>;

type OverrideKey = keyof typeof ICON_CLASS_OVERRIDES;

/**
 * Derive asset class(es) from icon identifier.
 * Uses auto-mapping with manual overrides for exceptions.
 *
 * @param iconId - Icon identifier in format "source:name" (e.g., "ad:User")
 *                 or category-scoped format "category::source:name" (e.g., "internet::ad:User")
 * @returns Array of asset class strings to use for filtering
 *
 * @example
 * getAssetClassesForIcon('ad:User') // ['user']
 * getAssetClassesForIcon('aws:Lambda') // ['aws']
 * getAssetClassesForIcon('lucide:Star') // []
 * getAssetClassesForIcon('internet::ad:User') // ['user'] (unscoped first)
 */
export function getAssetClassesForIcon(iconId: string): string[] {
  if (!iconId) return [];

  // Remove category scope prefix if present (e.g., 'internet::ad:User' -> 'ad:User')
  const unscopedId = removeCategoryScope(iconId);

  // 1. Check overrides first
  if (unscopedId in ICON_CLASS_OVERRIDES) {
    return [...ICON_CLASS_OVERRIDES[unscopedId as OverrideKey]];
  }

  // 2. Parse icon ID
  const colonIndex = unscopedId.indexOf(':');
  if (colonIndex === -1) return [];

  const source = unscopedId.slice(0, colonIndex);
  const name = unscopedId.slice(colonIndex + 1);

  if (!source || !name) return [];

  // 3. AD icons: ad:User -> user
  if (source === 'ad') {
    return [name.toLowerCase()];
  }

  // 4. Cloud icons: aws:Lambda -> aws (parent class)
  if (['aws', 'azure', 'gcp'].includes(source)) {
    return [source];
  }

  // 5. No filter for other icons
  return [];
}

/**
 * Check if icon has any class mappings (for UI hints)
 *
 * @param iconId - Icon identifier
 * @returns true if icon maps to at least one asset class
 */
export function iconHasClassFilter(iconId: string): boolean {
  return getAssetClassesForIcon(iconId).length > 0;
}

/**
 * Asset type with class property for sorting
 */
interface AssetWithClass {
  key: string;
  class?: string;
  [key: string]: unknown;
}

/**
 * Sort assets by class match priority.
 * Matching classes appear first, all assets still included.
 *
 * @param assets - Array of assets to sort
 * @param priorityClasses - Classes to prioritize (sorted to top)
 * @returns New array with matching assets first, then non-matching
 *
 * @example
 * const sorted = sortAssetsByClassMatch(assets, ['user', 'computer']);
 * // Assets with class 'user' or 'computer' appear first
 */
export function sortAssetsByClassMatch<T extends AssetWithClass>(
  assets: T[],
  priorityClasses: string[]
): T[] {
  if (priorityClasses.length === 0) return assets;

  const normalizedPriority = priorityClasses.map(c => c.toLowerCase());

  const matching: T[] = [];
  const nonMatching: T[] = [];

  for (const asset of assets) {
    const assetClass = asset.class?.toLowerCase() ?? '';
    if (normalizedPriority.includes(assetClass)) {
      matching.push(asset);
    } else {
      nonMatching.push(asset);
    }
  }

  return [...matching, ...nonMatching];
}
